import { ref, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'

/**
 * 后台空闲超时自动退出登录
 * @param {number} timeoutMinutes 超时时间（分钟），默认 30 分钟
 * @param {number} warningMinutes 提前警告时间（分钟），默认 5 分钟前提示
 */
export function useIdleTimeout(timeoutMinutes = 30, warningMinutes = 5) {
  const router = useRouter()
  const timeoutMs = timeoutMinutes * 60 * 1000
  const warningMs = (timeoutMinutes - warningMinutes) * 60 * 1000

  let idleTimer = null
  let warningTimer = null
  const showWarning = ref(false)
  const remainingSeconds = ref(warningMinutes * 60)
  let countdownTimer = null

  // 用户活动事件
  const activityEvents = [
    'mousedown',
    'mousemove',
    'keydown',
    'scroll',
    'touchstart',
    'click',
    'wheel',
  ]

  const clearTimers = () => {
    if (idleTimer) {
      clearTimeout(idleTimer)
      idleTimer = null
    }
    if (warningTimer) {
      clearTimeout(warningTimer)
      warningTimer = null
    }
    if (countdownTimer) {
      clearInterval(countdownTimer)
      countdownTimer = null
    }
  }

  const logout = () => {
    clearTimers()
    showWarning.value = false
    localStorage.removeItem('adminUser')
    // 使用标记避免守卫提示
    sessionStorage.setItem('idleLogout', '1')
    router.push('/admin/login')
  }

  const startCountdown = () => {
    remainingSeconds.value = warningMinutes * 60
    countdownTimer = setInterval(() => {
      remainingSeconds.value--
      if (remainingSeconds.value <= 0) {
        logout()
      }
    }, 1000)
  }

  const resetTimers = () => {
    // 如果警告已显示，则隐藏并停止倒计时
    if (showWarning.value) {
      showWarning.value = false
      if (countdownTimer) {
        clearInterval(countdownTimer)
        countdownTimer = null
      }
    }

    clearTimers()

    // 警告计时器
    warningTimer = setTimeout(() => {
      showWarning.value = true
      startCountdown()
    }, warningMs)

    // 强制退出计时器（兜底，即使倒计时出问题也会退出）
    idleTimer = setTimeout(() => {
      logout()
    }, timeoutMs)
  }

  // 节流：避免频繁触发
  let lastReset = 0
  const throttledReset = () => {
    const now = Date.now()
    if (now - lastReset > 10000) {
      // 10秒内只重置一次
      lastReset = now
      resetTimers()
    }
  }

  const continueSession = () => {
    showWarning.value = false
    if (countdownTimer) {
      clearInterval(countdownTimer)
      countdownTimer = null
    }
    resetTimers()
  }

  onMounted(() => {
    activityEvents.forEach((event) => {
      window.addEventListener(event, throttledReset, { passive: true })
    })
    resetTimers()
  })

  onUnmounted(() => {
    activityEvents.forEach((event) => {
      window.removeEventListener(event, throttledReset)
    })
    clearTimers()
  })

  return {
    showWarning,
    remainingSeconds,
    continueSession,
    logoutNow: logout,
  }
}
